/*
 * Copyright (c) 2022 biocrates life sciences ag, All rights reserved.
 */

import React, {useEffect, useRef} from "react"

import {useDispatch, useSelector} from "react-redux"

import {
    ColumnApi,
    ColumnResizedEvent,
    FilterChangedEvent,
} from "ag-grid-community"
import {AgGridReact} from "ag-grid-react"
import _ from "lodash"
import "ag-grid-community/dist/styles/ag-grid.css"
import "ag-grid-community/dist/styles/ag-theme-balham.css"
import {SortChangedEvent} from "ag-grid-community/dist/lib/events"
import {createSelector} from "reselect"

import {ColumnWidth} from "../../models/columnWidth"
import {TableSorting} from "../../models/tableSorting"

import {
    tableCellCellClass,
    tableCellCellStyle,
    tableCellRenderer,
    tooltipValueGetter,
} from "./cellRenderers"
import SortableTableProps from "./sortableTableProps"

import "../../../styles/ag-grid-custom.scss"
import {
    getFilterModelFromTableFiltering,
    getTableDefFromRawSortableTableData,
    getTableDefFromSortableTableData,
    getTableFilteringFromEvent,
    getTableSortingFromEvent,
} from "../../utils/tableUtils"
import {TableFiltering} from "../../models/tableFiltering"
import SortableTableData from "../../models/sortableTableData"

// must cast as any to set property on window
const globalThis = (window /* browser */ || global) /* node */ as any

const SortableTable: React.FC<SortableTableProps> = ({
    numFrozenColumns = 0,
    sortableDataSelector,
    setSortingAction,
    setFilteringAction,
    setColumnWidthsAction,
    customValueGetter,
    disableInternalSorting = true,
    disableCustomRenderer = false,
    suppressColumnVirtualisation = false,
    frameworkComponents = {},
    loadingOverlayComponent = "",
    defaultColDef = {width: 110, resizable: true},
    rowHeight,
    rowSelection,
    suppressCellSelection = true,
    onGridReady,
}) => {
    const dispatch = useDispatch()
    const gridRef = useRef<any>(null)
    const colWidths = useRef<ColumnWidth[]>([])
    const now = useRef(0) // to be used to differentiate right-click callback between multiple tables

    const selector = createSelector([sortableDataSelector], (data) =>
        data instanceof SortableTableData
            ? getTableDefFromSortableTableData(
                  data,
                  numFrozenColumns,
                  customValueGetter,
                  disableInternalSorting
              )
            : getTableDefFromRawSortableTableData(
                  data,
                  numFrozenColumns,
                  disableInternalSorting
              )
    )

    const tableDef = useSelector(selector, (a, b) => _.isEqual(a.rows, b.rows))

    // Ensure unique global function for right-click events in case of multiple tables
    useEffect(() => {
        now.current = Date.now() + Math.floor(Math.random() * 10000)
    }, [])

    useEffect(() => {
        // Apply settings filters on state change
        if (gridRef.current && setFilteringAction && tableDef.filtering) {
            const filterModel = getFilterModelFromTableFiltering(
                tableDef.filtering
            )
            // Set filter model after slight delay to avoid conflicts with sorting application
            const {api} = gridRef.current
            setTimeout(() => api.setFilterModel(filterModel), 100)
        }
        // Store latest widths settings separately as only row updates cause rerender
        if (tableDef.colWidths) {
            colWidths.current = [...tableDef.colWidths]
        }
    }, [tableDef])

    const onSortChanged = (event: SortChangedEvent) => {
        const colState = event.columnApi.getColumnState()
        // Check if sorting was changed by user or by external data (then, not all sorted columns may exist anymore)
        if (
            !_.isEqual(
                colState.map((cs) => ({
                    colId: cs.colId,
                    sort: cs.sort,
                    sortIndex: cs.sortIndex,
                })),
                tableDef.cols.map((col) => ({
                    colId: col.field,
                    sort: col.sort,
                    sortIndex: col.sortIndex,
                }))
            )
        ) {
            const tableSorting: TableSorting[] = getTableSortingFromEvent(event)
            // Dispatch sorting action only if done by user
            dispatch(setSortingAction(tableSorting))
        }
    }

    const onFilterChanged = (event: FilterChangedEvent) => {
        const tableFiltering: TableFiltering[] = getTableFilteringFromEvent(
            event
        )
        // Dispatch actions if filter change comes from user
        if (
            !_.isEqual(tableFiltering, tableDef.filtering) &&
            setFilteringAction
        ) {
            dispatch(setFilteringAction(tableFiltering))
        }
    }

    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished && event.column && setColumnWidthsAction) {
            // Update widths in settings, don't copy columns from existing table (not all cols may exist)
            const colId = event.column.getColId()
            const colNewWidth = event.column.getActualWidth()
            if (colNewWidth === defaultColDef.width) {
                // remove column from settings
                colWidths.current = [
                    ...colWidths.current.filter((colWidth) => !colWidth[colId]),
                ]
            } else if (colWidths.current.find((colWidth) => colWidth[colId])) {
                // replace existing setting
                colWidths.current = [
                    ...colWidths.current.map((colWidth) =>
                        colWidth[colId] ? {[colId]: colNewWidth} : colWidth
                    ),
                ]
            } else {
                // add new setting
                colWidths.current = [
                    ...colWidths.current,
                    {[colId]: colNewWidth},
                ]
            }
            dispatch(setColumnWidthsAction([...colWidths.current]))
        }
    }

    const onColumnSizeReset = (columnId: string) => {
        if (defaultColDef.width) {
            ;(gridRef.current?.columnApi as ColumnApi)?.setColumnWidth(
                columnId,
                defaultColDef.width
            )
        }
    }

    // Workaround for right-click functionality for header cells
    globalThis[`onColumnHeaderRightClick${now.current}`] = (
        event: PointerEvent
    ) => {
        const columnId = (event.target as Element)
            ?.closest(".ag-header-cell")
            ?.getAttribute("col-id")
        if (columnId) {
            onColumnSizeReset(columnId)
        }
        event.preventDefault()
    }

    const headerTemplate = `<div oncontextmenu="onColumnHeaderRightClick${now.current}(event)" class="ag-cell-label-container" role="presentation">
            <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
            <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
             </div>
        </div>`

    return (
        <div
            className="ag-theme-balham"
            style={{height: "100%", width: "100%"}}
        >
            <AgGridReact
                ref={gridRef}
                onGridReady={onGridReady}
                components={{
                    ...(!disableCustomRenderer && {
                        tableCellRenderer,
                    }),
                }}
                frameworkComponents={frameworkComponents}
                loadingOverlayComponent={loadingOverlayComponent}
                suppressFieldDotNotation
                rowData={tableDef.rows}
                columnDefs={tableDef.cols}
                rowSelection={rowSelection}
                rowHeight={rowHeight}
                // === Options to disable col & row lazy rendering (leads to longer loading and blank screen)
                // rowBuffer={999}
                // suppressMaxRenderedRowRestriction
                suppressColumnVirtualisation={suppressColumnVirtualisation}
                // suppressAnimationFrame // this one makes scrollbar laggier but all cells will be always rendered
                // onFirstDataRendered={() => setInitialGridReady(true)} // was needed with initial slow rendering where cells are empty first
                // === End of lazy rendering options
                defaultColDef={{
                    sortable: true,
                    ...(setFilteringAction && {
                        filter: "agTextColumnFilter",
                        filterParams: {
                            defaultJoinOperator: "OR",
                            buttons: ["apply", "reset"],
                        },
                    }),
                    ...(!disableCustomRenderer && {
                        cellRenderer: "tableCellRenderer",
                    }),
                    cellStyle: tableCellCellStyle,
                    cellClass: tableCellCellClass,
                    headerComponentParams: {template: headerTemplate},
                    tooltipValueGetter,
                    ...defaultColDef,
                }}
                onSortChanged={onSortChanged}
                onFilterChanged={onFilterChanged}
                onColumnResized={onColumnResized}
                popupParent={document.querySelector("body") as HTMLElement}
                multiSortKey="ctrl"
                tooltipShowDelay={400}
                suppressCellSelection={suppressCellSelection}
                suppressMovableColumns
                suppressScrollOnNewData
                // === Options for immutable data mode (disabled for now since suppressScrollOnNewData gives the same result)
                // immutableData={true}
                // getRowNodeId={getDataRowId}
                // === End of immutable data options
            />
        </div>
    )
}

const MemoizedSortableTable = React.memo(SortableTable)

export default MemoizedSortableTable
